/**
 * Global axios interceptor — auto-refreshes the access token on any 401 response
 * and retries the original request. Makes the whole app resilient to session
 * expiry / cookie drops without any per-page handling.
 *
 * Imported once at app boot (App.js).
 */
import axios from "axios";
import API_URL from "./api";
import logger from "./logger";

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
    failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve()));
    failedQueue = [];
};

axios.defaults.withCredentials = true;

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;

        // Skip refresh for the refresh endpoint itself + login + public endpoints
        const url = originalRequest?.url || "";
        const isAuthFlow = url.includes("/api/auth/refresh") || url.includes("/api/auth/login") || url.includes("/api/public/");

        if (status === 401 && !originalRequest?._retry && !isAuthFlow) {
            if (isRefreshing) {
                // Queue this request until the in-flight refresh resolves
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: () => resolve(axios({ ...originalRequest, _retry: true })),
                        reject,
                    });
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
                processQueue(null);
                return axios({ ...originalRequest, _retry: true });
            } catch (refreshErr) {
                processQueue(refreshErr);
                logger.error("Session refresh failed — redirecting to login", refreshErr);
                // Hard redirect ensures clean state. Skip if already on /login or /join.
                const path = window.location.pathname;
                if (!path.startsWith("/login") && !path.startsWith("/join")) {
                    window.location.href = "/login";
                }
                return Promise.reject(refreshErr);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    },
);

export default axios;
